import type { ClientArticleProps } from "@core/SitePresenter/SitePresenter";
import FetchService from "@core-ui/ApiServices/FetchService";
import MimeTypes from "@core-ui/ApiServices/Types/MimeTypes";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import ModalToOpenService from "@core-ui/ContextServices/ModalToOpenService/ModalToOpenService";
import ModalToOpen from "@core-ui/ContextServices/ModalToOpenService/model/ModalsToOpen";
import { useApi } from "@core-ui/hooks/useApi";
import type { ProviderItemProps } from "@ext/articleProvider/models/types";
import t from "@ext/localization/locale/translate";
import type { TemplateContentWarningProps } from "@ext/templates/components/TemplateContentWarning";
import TemplateService from "@ext/templates/components/TemplateService";
import { useSearchableMenu } from "@ui-kit/Dropdown";
import { useCallback, useMemo, useState } from "react";

const useApplyTemplate = (itemRefPath: string) => {
	const apiUrlCreator = ApiUrlCreatorService.value;

	const { call: getArticleContent } = useApi<string>({
		url: (api) => api.getArticleContent(itemRefPath),
	});

	const { call: getItemProps } = useApi<ClientArticleProps>({
		url: (api) => api.getItemProps(itemRefPath),
	});

	const setAsTemplate = useCallback(
		async (item: ProviderItemProps) => {
			ModalToOpenService.setValue(ModalToOpen.Loading);
			const itemProps = await getItemProps();
			if (!itemProps) return ModalToOpenService.resetValue();

			itemProps.template = item.id;
			itemProps.title = itemProps.title !== t("article.no-name") ? itemProps.title : "";

			await FetchService.fetch(apiUrlCreator.setArticleContent(itemRefPath), "");
			await FetchService.fetch(apiUrlCreator.updateItemProps(), JSON.stringify(itemProps), MimeTypes.json);

			ModalToOpenService.resetValue();
			await refreshPage();
		},
		[itemRefPath, getItemProps],
	);

	return useCallback(
		async (item: ProviderItemProps) => {
			const content = await getArticleContent();
			if (!content?.length) return setAsTemplate(item);

			ModalToOpenService.setValue<TemplateContentWarningProps>(ModalToOpen.TemplateContentWarning, {
				initialIsOpen: true,
				templateName: item.title,
				action: () => {
					void setAsTemplate(item);
				},
				onClose: () => {
					ModalToOpenService.resetValue();
				},
			});
		},
		[getArticleContent, setAsTemplate],
	);
};

export const useTemplatePicker = (itemRefPath: string) => {
	const apiUrlCreator = ApiUrlCreatorService.value;
	const { templates: templatesById } = TemplateService.value;
	const templates = useMemo(() => Array.from(templatesById.values()), [templatesById]);
	const [isReady, setIsReady] = useState(() => templatesById.size > 0);

	const fetchTemplates = useCallback(async () => {
		try {
			await TemplateService.fetchItems(apiUrlCreator);
		} finally {
			setIsReady(true);
		}
	}, []);

	const applyTemplate = useApplyTemplate(itemRefPath);
	const searchableMenu = useSearchableMenu();
	const { setSearch, filterItems } = searchableMenu;

	const filteredTemplates = useMemo(
		() => filterItems(templates.map((template) => ({ ...template, label: template.title ?? "" }))),
		[templates, filterItems],
	);

	const onOpenChange = useCallback(
		(open: boolean) => {
			if (!open) return setSearch("");
			void fetchTemplates();
		},
		[fetchTemplates, setSearch],
	);

	return {
		...searchableMenu,
		templates,
		filteredTemplates,
		isReady,
		fetchTemplates,
		applyTemplate,
		onOpenChange,
	};
};

export type TemplatePicker = ReturnType<typeof useTemplatePicker>;
